












































































































































































































/* General */
import Vue from 'vue'
import CardCanvas from '@/layouts/card-canvas.vue'

/* Mixins */

/* Components */
import Draggable from 'vuedraggable'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'
import RouteAssistant from '@/helpers/route-assistant'
import TextAssistant from '@/helpers/text-assistant'
import TypeAssistant from '@/helpers/type-assistant'
import WorkflowsAssistant from '@/helpers/workflows-assistant'

/* Data types */
import {
  IJob,
  IJobAssigneeMandate,
  IProjectWithAccessType,
  IWorkflowDetail,
} from '@/api/types/response.types'
import {
  IWorkflowsInputEditNameAndDescription,
  IWorkflowsInputModifyStatuses,
  IWorkflowsInputModifyStatusesStatus,
} from '@/api/types/request.types'

/* API */
import WorkflowsAPI from '@/api/modules/workflows.api'
import { EEntities } from '@/types'

export default Vue.extend({
  name: 'WorkflowDetail',

  metaInfo(): any {
    return {
      title: this.workflow.name,
    }
  },

  components: {
    CardCanvas,
    Draggable,
  },

  data() {
    return {
      ColorAssistant,
      RouteAssistant,
      TextAssistant,
      TypeAssistant,
      WorkflowsAssistant,
      isWorkflowDeleteable: false,
      canUnpublishWorkflow: false,
      isInitialLoading: true,
      isLoading: true,
      isCloning: false,
      isDeleting: false,
      isUnpublishing: false,
      isPublishing: false,
      isModifyingStatuses: false,
      isSubmittingNameAndDescription: false,
      isPostInitialLoad: false,
      showEditNameAndDescription: false,
      shouldSaveWorkflow: false,
      workflow: {} as IWorkflowDetail,
      entitiesWithWorkflow: [] as IProjectWithAccessType[],
      statuses: [] as IWorkflowsInputModifyStatusesStatus[],
      jobs: [] as IJob[],
      assigneeMandates: [] as IJobAssigneeMandate[],

      editableWorkflowName: '',
      editableWorkflowDescription: '',

      showStatusTransitionsModal: false,
      selectedStatusIDForStatusTransitionUpdate: null,

      maxStatusesAllowed: 40,

      enableJobs: false,
      showManageJobsDrawer: false,
    }
  },

  computed: {
    workflowID(): number {
      return Number(this.$route.params.id)
    },
    isBuiltIn(): boolean {
      return !this.workflow.isEditable
    },
    isCloneable(): boolean {
      return WorkflowsAssistant.isCloneable(this.workflow.name)
    },
    hasOverMinimumRequiredStatuses(): boolean {
      return this.statuses.length - 1 >= 2
    },
    disableSaveWorkflowButton(): boolean {
      return !this.shouldSaveWorkflow
    },
    disableNameAndDescriptionUpdate(): boolean {
      return this.editableWorkflowName.length === 0
    },
    hasEntitiesWithWorkflow(): boolean {
      return this.entitiesWithWorkflow.length > 0
    },
    entitiesWithWorkflowText(): string {
      const entityTypeName =
        this.entitiesWithWorkflow.length === 1
          ? this.entityTypeNameSingular.toLowerCase()
          : this.entityTypeNamePlural.toLowerCase()

      return `${
        this.entitiesWithWorkflow.length
      } ${entityTypeName} using this workflow`
    },
    isForMailbox(): boolean {
      return this.entityType === EEntities.MAILBOXES
    },
    entityType(): EEntities {
      return TypeAssistant.getEntityType(this.workflow.entityTypeID)
    },
    entityTypeNameSingular(): string {
      return TextAssistant.entitySingularName(this.entityType)
    },
    entityTypeNamePlural(): string {
      return TextAssistant.entityPluralName(this.entityType)
    },
    itemTypeNameSingular(): string {
      return TextAssistant.itemSingularName(this.entityType)
    },
    itemTypeNamePlural(): string {
      return TextAssistant.itemPluralName(this.entityType)
    },
    btnColor(): string {
      return ColorAssistant.getEntityColorByEntityTypeID(
        this.workflow.entityTypeID,
      )
    },
    hasJobs(): boolean {
      return this.jobs.length > 0
    },
    allowJobsManagement(): boolean {
      return this.$permissions.jobsFeatureEnabled && !this.isBuiltIn
    },
  },

  watch: {
    statuses: {
      deep: true,
      handler() {
        if (!this.isLoading) {
          this.shouldSaveWorkflow = true
        }
      },
    },
    showStatusTransitionsModal: {
      immediate: false,
      handler(val) {
        if (!val) {
          this.selectedStatusIDForStatusTransitionUpdate = null
        }
      },
    },
  },

  async created() {
    this.isInitialLoading = true
    await this.fetchAllData()
    this.isInitialLoading = false
    await this.$nextTick()
    this.isPostInitialLoad = true
  },

  async beforeRouteLeave(to, from, next) {
    if (this.shouldSaveWorkflow) {
      try {
        await this.confirmSave()
        const success = await this.saveWorkflow()
        if (success) {
          next()
        }
      } catch {
        next()
      }
    } else {
      next()
    }
  },

  methods: {
    async confirmSave() {
      await this.$confirm(`You've modified some statuses for this workflow.`, {
        dangerouslyUseHTMLString: true,
        center: true,
        confirmButtonText: 'Save updates',
        confirmButtonClass: 'el-button--primary',
        cancelButtonText: 'Discard changes',
        cancelButtonClass: 'el-button--cancel',
        type: 'warning',
        title: `Save these updates?`,
      })
    },
    removeStatus(index: number) {
      this.statuses.splice(index, 1)
    },
    addAdditionalStatus() {
      this.statuses.push({
        id: null,
        name: '',
        color: ColorAssistant.DefaultStatusColorOptions,
        isInactive: false,
        assigneeMandateJobID: this.hasJobs ? this.jobs[0].id : null,
      })
    },
    showStatusTransitions(statusID: number) {
      this.selectedStatusIDForStatusTransitionUpdate = statusID
      this.$nextTick(() => {
        this.showStatusTransitionsModal = true
      })
    },
    cancelSaveWorkflow() {
      this.shouldSaveWorkflow = false
      this.goToWorkflowsHome()
    },
    resetSaveDataCheck() {
      this.shouldSaveWorkflow = false
    },
    async handleStatusTransitionUpdate() {
      await this.refreshStatusTransitions()
    },
    goToWorkflowsHome() {
      RouteAssistant.goToWorkflowsHome(
        this.$router,
        this.$route.params.accountId,
      )
    },
    getJobForStatusID(statusID: number) {
      const mandate = this.assigneeMandates.find(am => am.statusID === statusID)

      if (mandate) {
        return mandate.jobID
      }

      return null
    },
    async handleJobsUpdate() {
      await this.getDetail()
      this.shouldSaveWorkflow = false
    },
    async fetchAllData() {
      this.isLoading = true
      await Promise.all([this.getDetail(), this.getEntitiesOnWorkflow()])
      this.isLoading = false
    },
    /* Returns success boolean for handling on beforeRouteLeave() */
    async saveWorkflow(showSuccess: boolean = false): Promise<boolean> {
      this.shouldSaveWorkflow = false

      try {
        this.isModifyingStatuses = true

        const data = {
          listStatuses: this.statuses,
        } as IWorkflowsInputModifyStatuses

        const response = await WorkflowsAPI.put_ModifyStatuses(
          this.workflow.id,
          data,
        )
        if (showSuccess) {
          this.$messaging.showSuccess(response.message)
          await this.fetchAllData()
        }
        return true
      } catch (err) {
        this.$error.emit(err, true)
        return false
      } finally {
        this.isModifyingStatuses = false
      }
    },
    async getDetail() {
      try {
        const response = await WorkflowsAPI.get_Detail(this.workflowID)
        this.workflow = response.detail
        this.isWorkflowDeleteable = response.isDeleteable
        this.canUnpublishWorkflow = response.canUnpublish

        this.editableWorkflowName = response.detail.name
        this.editableWorkflowDescription = response.detail.description

        this.resetSaveDataCheck()

        this.statuses = [] as IWorkflowsInputModifyStatusesStatus[]

        this.workflow.listStatuses.forEach(s => {
          const curAssigneeMandate = this.workflow.listAssigneeMandates.find(
            am => am.statusID === s.id,
          )
          this.statuses.push({
            id: s.id,
            name: s.name,
            color: s.color,
            isInactive: this.workflow.listInactiveStatuses.some(
              as => as.id === s.id,
            ),
            assigneeMandateJobID: curAssigneeMandate
              ? curAssigneeMandate.jobID
              : null,
          })
        })

        this.assigneeMandates = this.workflow.listAssigneeMandates
        this.jobs = this.workflow.listJobs
      } catch (err) {
        this.$error.emit(err)
      }
    },
    async refreshStatusTransitions() {
      try {
        const response = await WorkflowsAPI.get_Detail(this.workflowID)
        this.workflow.listStatusTransitions =
          response.detail.listStatusTransitions
      } catch (err) {
        this.$error.emit(err)
      }
    },
    async getEntitiesOnWorkflow() {
      try {
        this.entitiesWithWorkflow = await WorkflowsAPI.get_Projects(
          this.workflowID,
        )
      } catch (err) {
        this.$error.emit(err)
      }
    },
    async cloneWorkflow() {
      try {
        this.isCloning = true
        const response = await WorkflowsAPI.post_Clone(this.workflow.id)
        RouteAssistant.goToWorkflowDetail(
          this.$router,
          this.$route.params.accountId,
          response.id,
        )
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isCloning = false
      }
    },
    async handleDeleteWorkflow() {
      // If deleting, undo the need to save...
      this.resetSaveDataCheck()

      await this.$confirm(
        `<strong>This cannot be undone.</strong>
        <br>Are you sure you want to delete this?`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: 'Delete this workflow?',
        },
      )
      await this.deleteWorkflow()
      this.goToWorkflowsHome()
    },
    async deleteWorkflow() {
      try {
        this.isDeleting = true
        const response = await WorkflowsAPI.delete_Delete(this.workflow.id)
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isDeleting = false
      }
    },
    async unpublishWorkflow() {
      this.resetSaveDataCheck()
      try {
        this.isUnpublishing = true
        await WorkflowsAPI.put_Unpublish(this.workflow.id)
        this.goToWorkflowsHome()
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isUnpublishing = false
      }
    },
    async publishWorkflow() {
      try {
        this.isPublishing = true
        if (this.shouldSaveWorkflow) {
          this.saveWorkflow()
        }
        await WorkflowsAPI.put_Publish(this.workflow.id)
        this.goToWorkflowsHome()
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isPublishing = false
      }
    },
    async editNameAndDescription() {
      try {
        this.isSubmittingNameAndDescription = true
        const data = {
          name: this.editableWorkflowName,
          description: this.editableWorkflowDescription,
        } as IWorkflowsInputEditNameAndDescription
        const response = await WorkflowsAPI.put_EditNameAndDescription(
          this.workflow.id,
          data,
        )
        this.workflow.name = this.editableWorkflowName
        this.workflow.description = this.editableWorkflowDescription
        this.showEditNameAndDescription = false
        this.$messaging.showSuccess(response.message)
      } catch (err) {
        this.$error.emit(err, true)
      } finally {
        this.isSubmittingNameAndDescription = false
      }
    },
    sendToEntityHome(entity: IProjectWithAccessType) {
      if (this.isForMailbox) {
        RouteAssistant.goToMailbox(
          this.$router,
          this.$route.params.accountId,
          entity.id,
        )
      } else {
        RouteAssistant.goToProjectLanding(
          this.$router,
          this.$route.params.accountId,
          entity.id,
          this.$permissions,
        )
      }
    },
  },
})
